import React, { useEffect, useState, useCallback } from 'react';
import { fmtDateTime } from '../utils/dateUtils';

const today = () => new Date().toISOString().split('T')[0];

function PmsUserLoginActivity({ token }) {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());

  const hdrs = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: '500', from, to });
      const res = await fetch(`/api/auth/admin/user-login-activity?${params}`, { headers: hdrs() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load login activity');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load login activity');
    } finally {
      setLoading(false);
    }
  }, [from, to, hdrs]);

  useEffect(() => { load(); }, [load]);

  const failedCount = rows.filter(r => !r.success).length;
  const successCount = rows.filter(r => r.success).length;
  const filteredRows = statusFilter === 'all' ? rows : statusFilter === 'failed' ? rows.filter(r => !r.success) : rows.filter(r => r.success);

  const filterStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.8rem 1rem', background: '#f8fafc', borderRadius: 8, border: '1px solid #e5e7eb' };
  const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#475569' };
  const inputStyle = { padding: '0.4rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.82rem', outline: 'none' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.3rem' }}>User Login History</h2>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Activity audit trail — when each platform user logged in, from where, on what device/browser, and which hotel(s) they accessed that session.
      </p>
      {error && <div className="error-banner">{error}</div>}

      <div style={filterStyle}>
        <label style={labelStyle}>From</label>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        <label style={labelStyle}>To</label>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        <button onClick={load} className="btn-success" style={{ padding: '0.4rem 1rem', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem' }}>
          Fetch
        </button>
        {loading && <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Loading...</span>}
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ padding: '0.6rem 1.2rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#166534' }}>{successCount}</div>
          <div style={{ fontSize: '0.75rem', color: '#166534' }}>Successful</div>
        </div>
        <div style={{ padding: '0.6rem 1.2rem', background: failedCount > 0 ? '#fef2f2' : '#f9fafb', border: `1px solid ${failedCount > 0 ? '#fecaca' : '#e5e7eb'}`, borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: failedCount > 0 ? '#991b1b' : '#6b7280' }}>{failedCount}</div>
          <div style={{ fontSize: '0.75rem', color: failedCount > 0 ? '#991b1b' : '#6b7280' }}>Failed Attempts</div>
        </div>
        <div style={{ padding: '0.6rem 1.2rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, minWidth: 120 }}>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e40af' }}>{rows.length}</div>
          <div style={{ fontSize: '0.75rem', color: '#1e40af' }}>Total</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem' }}>
        {[
          { key: 'all', label: `All (${rows.length})` },
          { key: 'failed', label: `Failed (${failedCount})` },
          { key: 'success', label: `Success (${successCount})` },
        ].map(f => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            style={{
              padding: '0.35rem 0.8rem', borderRadius: 6, border: '1px solid',
              borderColor: statusFilter === f.key ? (f.key === 'failed' ? '#ef4444' : 'var(--theme-primary-dark)') : '#d1d5db',
              background: statusFilter === f.key ? (f.key === 'failed' ? '#ef4444' : 'var(--theme-primary-dark)') : '#fff',
              color: statusFilter === f.key ? '#fff' : '#374151',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
            }}
          >{f.label}</button>
        ))}
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f5f6fa' }}>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Time</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>User</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Role</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>IP Address</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Device</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Browser</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Hotel(s) Accessed</th>
              <th style={{ padding: '0.6rem', textAlign: 'left' }}>Logout At</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.id} style={{ borderTop: '1px solid #eef2f7', background: !row.success ? '#fef2f2' : undefined }}>
                <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.login_at ? fmtDateTime(row.login_at) : '-'}</td>
                <td style={{ padding: '0.6rem', fontWeight: 600 }}>
                  {row.username || row.login_identifier || '-'}
                  {!row.success && row.failure_reason && (
                    <div style={{ fontWeight: 400, fontSize: '0.72rem', color: '#991b1b' }}>{row.failure_reason}</div>
                  )}
                </td>
                <td style={{ padding: '0.6rem' }}>{row.role || '-'}</td>
                <td style={{ padding: '0.6rem' }}>
                  <span style={{
                    padding: '0.15rem 0.5rem', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600,
                    background: row.success ? '#dcfce7' : '#fecaca',
                    color: row.success ? '#166534' : '#991b1b',
                  }}>
                    {row.success ? 'SUCCESS' : 'FAILED'}
                  </span>
                </td>
                <td style={{ padding: '0.6rem', fontSize: '0.82rem', fontFamily: 'monospace' }}>{row.ip_address || '-'}</td>
                <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.device_type || '-'}{row.device_os ? ` / ${row.device_os}` : ''}</td>
                <td style={{ padding: '0.6rem', fontSize: '0.82rem' }} title={row.user_agent || ''}>
                  {row.browser_name ? `${row.browser_name}${row.browser_version ? ` ${row.browser_version}` : ''}` : '-'}
                </td>
                <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.properties_accessed || '-'}</td>
                <td style={{ padding: '0.6rem', fontSize: '0.82rem' }}>{row.logout_at ? fmtDateTime(row.logout_at) : '-'}</td>
              </tr>
            ))}
            {!filteredRows.length && (
              <tr>
                <td colSpan={9} style={{ padding: '0.8rem', color: '#7f8c8d' }}>No {statusFilter === 'failed' ? 'failed login attempts' : 'login activity'} found for the selected date range</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PmsUserLoginActivity;
